import 'dayjs/locale/sv';

import type { Dayjs } from 'dayjs';

import type { KomIgångStatus, HärleddSignal } from 'src/sections/kom-igang/kom-igang-typer';

// ----------------------------------------------------------------------
// Startsidans välkomstbanner, utan React.
//
// Allt som avgör vad bannern säger räknas ut här, så att både föreningsvyn och hubben säger
// samma sak om samma läge — och så att reglerna går att testa utan att rendera något.
// ----------------------------------------------------------------------

/** Hur långt fram remsan ser. Två månader rymmer nästa moms- och arbetsgivardeklaration. */
export const HORISONT_DAGAR = 60;

/** En förfallodag på remsan. Föreningsvyn mappar `AttGöraPost`, hubben `Åtgärdsrad`. */
export type Tidslinjepost = {
  id: string;
  titel: string;
  förfallodag: Dayjs;
  /** Föreningen posten gäller — visas bara i hubben, där flera föreningar delar remsan. */
  orgId?: string;
  orgNamn?: string;
  länk?: string;
  internLänk?: boolean;
};

export type Välkomstläge =
  | { typ: 'laddar' }
  /** Organisationen är nyss skapad och har inget eget innehåll än. */
  | { typ: 'förstaGången'; närmast?: Tidslinjepost }
  /** Hubben utan en enda organisation. */
  | { typ: 'ingenOrganisation' }
  | { typ: 'lugnt' }
  | {
      typ: 'väntar';
      /** Antalet saker som väntar, även de utan förfallodag (hubbens databasåtgärder). */
      antal: number;
      /** Hur många föreningar sakerna fördelar sig på. Bara intressant i hubben. */
      antalFöreningar: number;
      /** Bara poster med förfallodag, sorterade på den. */
      poster: Tidslinjepost[];
      försenade: Tidslinjepost[];
      närmast?: Tidslinjepost;
    };

// ----------------------------------------------------------------------

/**
 * Signalerna som onboardingen själv ger upphov till.
 *
 * En förening som bara har dem har gått igenom registreringen men inte lagt in något eget —
 * inget bankkonto, ingen verifikation, inga medlemmar. Det är den som ska mötas av
 * förstagångsläget. Att räkna "inga signaler alls" hade aldrig slagit till: grunddatan är
 * ifylld redan när föreningen skapas.
 */
const UPPSTARTSSIGNALER: ReadonlySet<HärleddSignal> = new Set<HärleddSignal>([
  'GRUNDDATA_IFYLLD',
  'STADGAR_FINNS',
  'RAKENSKAPSAR_FINNS',
  'MENYER_ANPASSADE',
]);

/** Sant när föreningen inte har något annat än det onboardingen lade dit. Okänd status = nej. */
export function ärNystartad(status: KomIgångStatus | undefined): boolean {
  if (!status) return false;
  return (
    status.avbockade.length === 0 && status.härledda.every((signal) => UPPSTARTSSIGNALER.has(signal))
  );
}

// ----------------------------------------------------------------------

type Underlag = {
  laddar: boolean;
  /** Poster utan förfallodag räknas med i antalet men hamnar inte på remsan. */
  poster: Array<Tidslinjepost | Omit<Tidslinjepost, 'förfallodag'> & { förfallodag?: Dayjs }>;
  idag: Dayjs;
  nystartad?: boolean;
  /** Bara hubben skickar med det; föreningsvyn har alltid en organisation. */
  antalOrganisationer?: number;
};

export function härledVälkomstläge({
  laddar,
  poster,
  idag,
  nystartad,
  antalOrganisationer,
}: Underlag): Välkomstläge {
  if (antalOrganisationer === 0) return { typ: 'ingenOrganisation' };
  // Hellre en skelettrad än en mening som byts ut ett ögonblick senare.
  if (laddar) return { typ: 'laddar' };

  const daterade = poster
    .filter((post): post is Tidslinjepost => Boolean(post.förfallodag))
    .sort((a, b) => a.förfallodag.valueOf() - b.förfallodag.valueOf());

  const försenade = daterade.filter((post) => post.förfallodag.isBefore(idag, 'day'));
  const kommande = daterade.filter((post) => !post.förfallodag.isBefore(idag, 'day'));

  if (nystartad) return { typ: 'förstaGången', närmast: kommande[0] };
  if (poster.length === 0) return { typ: 'lugnt' };

  return {
    typ: 'väntar',
    antal: poster.length,
    antalFöreningar: new Set(poster.map((post) => post.orgId).filter(Boolean)).size,
    poster: daterade,
    försenade,
    // Det försenade är mer bråttom än det kommande, och står därför först.
    närmast: försenade[0] ?? kommande[0],
  };
}

// ----------------------------------------------------------------------
// Remsan
// ----------------------------------------------------------------------

/** Alla poster samma dag delar en markering — två prickar på samma pixel säger ingenting. */
export type Tidslinjegrupp = {
  dag: Dayjs;
  dagarKvar: number;
  /** 0 = idag, 1 = remsans högra kant. */
  andel: number;
  poster: Tidslinjepost[];
};

export type Tidslinje = {
  försenade: Tidslinjepost[];
  grupper: Tidslinjegrupp[];
  /** Poster efter horisonten. Remsan visar bara antalet. */
  senare: number;
};

export function placeraPåTidslinjen(
  poster: Tidslinjepost[],
  idag: Dayjs,
  horisont: number = HORISONT_DAGAR
): Tidslinje {
  const start = idag.startOf('day');
  const försenade: Tidslinjepost[] = [];
  const perDag = new Map<number, Tidslinjegrupp>();
  let senare = 0;

  poster.forEach((post) => {
    const dagarKvar = post.förfallodag.startOf('day').diff(start, 'day');
    if (dagarKvar < 0) {
      försenade.push(post);
      return;
    }
    if (dagarKvar > horisont) {
      senare += 1;
      return;
    }
    const grupp = perDag.get(dagarKvar);
    if (grupp) {
      grupp.poster.push(post);
    } else {
      perDag.set(dagarKvar, {
        dag: post.förfallodag.startOf('day'),
        dagarKvar,
        andel: dagarKvar / horisont,
        poster: [post],
      });
    }
  });

  const grupper = [...perDag.values()].sort((a, b) => a.dagarKvar - b.dagarKvar);
  return { försenade, grupper, senare };
}

// ----------------------------------------------------------------------
// Texterna
// ----------------------------------------------------------------------

export function hälsning(läge: Välkomstläge, förnamn?: string): string {
  const första = läge.typ === 'förstaGången' || läge.typ === 'ingenOrganisation';
  if (första) return förnamn ? `Välkommen, ${förnamn}` : 'Välkommen';
  return förnamn ? `Hej ${förnamn}` : 'Hej';
}

/** "idag", "imorgon", "om 18 dagar", "för 3 dagar sedan". */
export function avstånd(dag: Dayjs, idag: Dayjs): string {
  const dagar = dag.startOf('day').diff(idag.startOf('day'), 'day');
  if (dagar === 0) return 'idag';
  if (dagar === 1) return 'imorgon';
  if (dagar === -1) return 'igår';
  if (dagar > 1) return `om ${dagar} dagar`;
  return `för ${-dagar} dagar sedan`;
}

export function datum(dag: Dayjs, idag: Dayjs): string {
  const format = dag.isSame(idag, 'year') ? 'D MMMM' : 'D MMMM YYYY';
  return dag.locale('sv').format(format);
}

/** Meningen under hälsningen. Säger vad som väntar, eller vad ytan är till för. */
export function sammanfattning(läge: Välkomstläge, idag: Dayjs): string {
  switch (läge.typ) {
    case 'laddar':
      return '';
    case 'ingenOrganisation':
      return 'Lägg till din förening eller ditt företag, så samlas ekonomi, fastighet och förfallodagar här.';
    case 'förstaGången': {
      const bas = 'Här samlas föreningens ekonomi, fastighet och medlemmar.';
      if (!läge.närmast) return `${bas} Kom igång-guiden visar vad som är bra att lägga in först.`;
      return `${bas} Första förfallodagen: ${läge.närmast.titel}, ${datum(läge.närmast.förfallodag, idag)}.`;
    }
    case 'lugnt':
      return 'Inget väntar just nu. Nya förfallodagar dyker upp här när de närmar sig.';
    case 'väntar': {
      const saker =
        (läge.antal === 1 ? '1 sak väntar' : `${läge.antal} saker väntar`) +
        (läge.antalFöreningar > 1 ? ` i ${läge.antalFöreningar} föreningar` : '');
      const { närmast } = läge;
      if (!närmast) return `${saker}.`;
      if (läge.försenade.length > 0) {
        const försenat = läge.försenade.length === 1 ? '1 är försenad' : `${läge.försenade.length} är försenade`;
        return `${saker}, och ${försenat}. Äldst: ${närmast.titel}, förföll ${datum(närmast.förfallodag, idag)}.`;
      }
      return `${saker}. Närmast: ${närmast.titel}, ${datum(närmast.förfallodag, idag)}.`;
    }
    default:
      return '';
  }
}
